/**
 * Shared filter options for architectural style and property condition.
 *
 * NOTE: CREA's `ArchitecturalStyle` and `PropertyCondition` array fields are
 * sparsely populated for Alberta listings — most properties never set them.
 * Real signal usually lives in the free-text `description` (and for "new
 * build", in `year_built`). To make these filters actually return results,
 * each option carries BOTH:
 *   - `variants`: exact strings to look up in the array field (array overlap)
 *   - `descriptionPatterns`: ILIKE substrings to scan the description
 *   - optional `extraOr`: raw PostgREST OR clauses (e.g. year_built >= X)
 *
 * The combined filter is emitted as a single PostgREST `.or()` so a match in
 * any source counts.
 */

export interface FilterOption {
  key: string;
  label: string;
  variants: string[];
  descriptionPatterns?: string[];
  extraOr?: string[];
}

const CURRENT_YEAR = new Date().getFullYear();

export const ARCHITECTURAL_STYLE_OPTIONS: FilterOption[] = [
  {
    key: 'bungalow',
    label: 'Bungalow',
    variants: ['Bungalow', 'bungalow', 'Raised bungalow', 'Raised Bungalow'],
    descriptionPatterns: ['bungalow'],
  },
  {
    key: 'bi_level',
    label: 'Bi-Level',
    variants: ['Bi-Level', 'Bi-level', 'Bi Level', 'Bilevel'],
    descriptionPatterns: ['bi-level', 'bi level', 'bilevel'],
  },
  {
    key: 'four_level_split',
    label: '4 Level Split',
    variants: ['4 Level Split', 'Four Level Split', '4-Level Split', '4 Level', 'Split Level'],
    descriptionPatterns: ['4 level split', 'four level split', '4-level split', 'split level'],
  },
  {
    key: 'two_storey',
    label: '2 Storey',
    variants: ['2 Storey', 'Two Storey', '2 Story', 'Two Story', '2 Level', 'Two Level', 'Multi-level', 'Multi-Level'],
    descriptionPatterns: ['2 storey', 'two storey', '2 story', 'two story', '2-storey', 'two-storey'],
  },
];

export const PROPERTY_CONDITION_OPTIONS: FilterOption[] = [
  {
    key: 'new_or_never_occupied',
    label: 'New / Never Occupied',
    variants: ['New Building', 'Never Occupied', 'New', 'Never occupied'],
    descriptionPatterns: [
      'never occupied',
      'never lived in',
      'brand new',
      'new build',
      'new construction',
      'newly built',
      'never been lived',
    ],
    extraOr: [`year_built.gte.${CURRENT_YEAR - 1}`],
  },
  {
    key: 'show_home',
    label: 'Show Home',
    variants: ['Show Home', 'Showhome', 'Show home'],
    descriptionPatterns: ['show home', 'showhome', 'show-home'],
  },
];


export function resolveStyleVariants(keys: string[] | undefined): string[] {
  if (!keys || keys.length === 0) return [];
  return ARCHITECTURAL_STYLE_OPTIONS.filter((o) => keys.includes(o.key)).flatMap((o) => o.variants);
}

export function resolveConditionVariants(keys: string[] | undefined): string[] {
  if (!keys || keys.length === 0) return [];
  return PROPERTY_CONDITION_OPTIONS.filter((o) => keys.includes(o.key)).flatMap((o) => o.variants);
}

/**
 * Quote a value for use inside a PostgREST `.or()` clause when it contains
 * characters that would otherwise confuse the parser (commas, spaces, parens).
 * Array-overlap values go inside `{...}` and need double quotes around strings
 * with spaces or punctuation.
 */
function quoteArrayValue(v: string): string {
  // Always quote — safe and avoids edge cases with spaces/dashes.
  return `"${v.replace(/"/g, '\\"')}"`;
}

function buildOrClauseForOptions(
  arrayColumn: 'architectural_style' | 'property_condition',
  options: FilterOption[],
): string | null {
  if (options.length === 0) return null;
  const clauses: string[] = [];

  const allVariants = options.flatMap((o) => o.variants);
  if (allVariants.length > 0) {
    const quoted = allVariants.map(quoteArrayValue).join(',');
    clauses.push(`${arrayColumn}.ov.{${quoted}}`);
  }

  for (const opt of options) {
    for (const pattern of opt.descriptionPatterns ?? []) {
      // PostgREST ilike uses `*` as wildcard. Avoid commas/parens in patterns.
      clauses.push(`description.ilike.*${pattern}*`);
    }
    for (const extra of opt.extraOr ?? []) {
      clauses.push(extra);
    }
  }

  return clauses.length > 0 ? clauses.join(',') : null;
}

export function buildStyleOrFilter(keys: string[] | undefined): string | null {
  if (!keys || keys.length === 0) return null;
  const opts = ARCHITECTURAL_STYLE_OPTIONS.filter((o) => keys.includes(o.key));
  return buildOrClauseForOptions('architectural_style', opts);
}

export function buildConditionOrFilter(keys: string[] | undefined): string | null {
  if (!keys || keys.length === 0) return null;
  const opts = PROPERTY_CONDITION_OPTIONS.filter((o) => keys.includes(o.key));
  return buildOrClauseForOptions('property_condition', opts);
}
